import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface DonutSegment {
    key: string
    label: string
    value: number
    color: string
}

interface DonutChartProps {
    segments: DonutSegment[]
    activeKey: string | null
    onActiveChange: (key: string | null) => void
    centerLabel: string
    centerValue: string
    ariaLabel: string
    className?: string
}

const SIZE = 120
const RADIUS = 48
const STROKE = 13
const GAP = 1.4
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

// Part to whole ring in the fixed plan order, with a surface gap between segments.
// The legend next to it carries every value, so color is never the only cue.
export const DonutChart: FC<DonutChartProps> = ({
    segments,
    activeKey,
    onActiveChange,
    centerLabel,
    centerValue,
    ariaLabel,
    className
}): ReactNode => {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0)
    const visible = segments.filter((segment) => segment.value > 0)
    let offset = 0

    return (
        <div className={cn('relative aspect-square w-full max-w-[220px]', className)}>
            <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className='size-full -rotate-90'
                role='img'
                aria-label={ariaLabel}
                onPointerLeave={() => onActiveChange(null)}
            >
                <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill='none' className='stroke-muted' strokeWidth={STROKE} />

                {total > 0 && visible.map((segment) => {
                    const length = (segment.value / total) * CIRCUMFERENCE
                    const dash = Math.max(0, visible.length > 1 ? length - GAP : length)
                    const isActive = activeKey === segment.key
                    const dimmed = activeKey !== null && !isActive
                    const element = (
                        <circle
                            key={segment.key}
                            cx={SIZE / 2}
                            cy={SIZE / 2}
                            r={RADIUS}
                            fill='none'
                            stroke={segment.color}
                            strokeWidth={isActive ? STROKE + 4 : STROKE}
                            strokeDasharray={`${dash.toFixed(3)} ${(CIRCUMFERENCE - dash).toFixed(3)}`}
                            strokeDashoffset={(-offset).toFixed(3)}
                            className={cn('cursor-pointer transition-[opacity,stroke-width] duration-150', dimmed && 'opacity-35')}
                            onPointerEnter={() => onActiveChange(segment.key)}
                        />
                    )

                    offset += length
                    return element
                })}
            </svg>

            <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center'>
                <span className='font-heading text-xl leading-tight font-semibold text-foreground tabular-nums sm:text-2xl'>
                    {centerValue}
                </span>
                <span className='mt-1 text-xs text-muted-foreground'>
                    {centerLabel}
                </span>
            </div>
        </div>
    )
}
